/**
 * Aykah Conversions — Micro-interactions for cart, wishlist, and purchase actions.
 *
 * Hooks into existing custom events (cart:updated, wishlist:updated) and adds
 * delightful (but quiet) feedback at every conversion touchpoint.
 *
 * Includes:
 *  1. Cart count-badge bounce when item added/removed
 *  2. Wishlist heart particle burst on add
 *  3. "Fly-to-cart" ghost from product image → cart icon on add
 *  4. Toast notification (bottom-right) for cart/wishlist actions
 *  5. Button press feedback on Buy Now / Add to Cart / Wishlist
 *  6. Free-shipping progress nudge when threshold approached/crossed
 *
 * All animations:
 *  - Use transform + opacity only (compositor-only, no layout thrash)
 *  - Respect prefers-reduced-motion
 *  - Bail silently if target elements aren't on the page
 */
(function () {
  'use strict';

  if (window.__aykahConversionsLoaded) return;
  window.__aykahConversionsLoaded = true;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ────────────────────────────────────────────────────────────
     1. CART COUNT BADGE BOUNCE
     Fires whenever cart-drawer.js dispatches `cart:updated`.
     Existing badge: #cart-drawer-badge in header.
     ──────────────────────────────────────────────────────────── */
  var lastCartCount = -1;
  function bumpCartBadge() {
    var badge = document.getElementById('cart-drawer-badge');
    if (!badge) return;
    var count = parseInt(badge.textContent, 10) || 0;
    /* Only bump if count INCREASED (not decreased) */
    if (lastCartCount === -1) { lastCartCount = count; return; }
    if (count <= lastCartCount) { lastCartCount = count; return; }
    lastCartCount = count;
    if (prefersReducedMotion) return;

    badge.style.transformOrigin = 'center';
    badge.style.transition = 'none';
    badge.style.transform = 'scale(0.6)';
    requestAnimationFrame(function () {
      badge.style.transition = 'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)';
      badge.style.transform = 'scale(1.35)';
      setTimeout(function () { badge.style.transform = 'scale(1)'; }, 250);
    });
  }

  /* ────────────────────────────────────────────────────────────
     2. WISHLIST HEART PARTICLE BURST
     Triggered when a wishlist button is clicked AND product is being ADDED
     (not removed). We hook into the click in capture phase but read state
     AFTER the existing handler updates it (microtask delay).
     ──────────────────────────────────────────────────────────── */
  function heartBurst(btn) {
    if (prefersReducedMotion) return;
    var rect = btn.getBoundingClientRect();
    var cx = rect.left + rect.width / 2;
    var cy = rect.top + rect.height / 2;

    /* Container for burst particles (cleaned up after animation) */
    var burst = document.createElement('div');
    burst.style.cssText = 'position:fixed;pointer-events:none;z-index:9999;left:0;top:0;';
    document.body.appendChild(burst);

    /* Pinkish-red bubble palette — matches the wishlist heart's text-brand-error */
    var COLORS = ['#d04545', '#ff5e7e', '#ff8fab', '#ffb3c1'];
    var COUNT = 9;
    var DIST = 40;
    for (var i = 0; i < COUNT; i++) {
      var dot = document.createElement('span');
      var angle = (Math.PI * 2 * i) / COUNT + (Math.random() - 0.5) * 0.4;
      var dist = DIST + Math.random() * 14;
      var dx = Math.cos(angle) * dist;
      var dy = Math.sin(angle) * dist;
      var size = 4 + Math.random() * 3;
      dot.style.cssText =
        'position:absolute;left:' + cx + 'px;top:' + cy + 'px;' +
        'width:' + size + 'px;height:' + size + 'px;border-radius:50%;' +
        'background:' + COLORS[i % COLORS.length] + ';' +
        'transform:translate(-50%,-50%) scale(0);' +
        'opacity:1;will-change:transform,opacity;' +
        'transition:transform 0.65s cubic-bezier(0.18,0.89,0.32,1.28),opacity 0.65s ease-out;';
      burst.appendChild(dot);
      /* trigger transition next frame */
      requestAnimationFrame(function (d, x, y) {
        return function () {
          d.style.transform = 'translate(calc(-50% + ' + x + 'px),calc(-50% + ' + y + 'px)) scale(1)';
          setTimeout(function () { d.style.opacity = '0'; d.style.transform = 'translate(calc(-50% + ' + (x * 1.4) + 'px),calc(-50% + ' + (y * 1.4) + 'px)) scale(0.4)'; }, 200);
        };
      }(dot, dx, dy));
    }

    /* Heart pulse on the button itself */
    var heart = btn.querySelector('svg');
    if (heart) {
      heart.style.transition = 'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)';
      heart.style.transform = 'scale(1.4)';
      setTimeout(function () { heart.style.transform = 'scale(1)'; }, 220);
    }

    setTimeout(function () { burst.remove(); }, 850);
  }

  /* The existing product-card-actions.js handler runs in CAPTURE phase and
     calls e.stopPropagation(), which prevents bubble-phase listeners. So we
     register on capture too (fires first because this script loads first),
     stash the button, and react when `wishlist:updated` fires. */
  var lastWishlistClickBtn = null;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.product-card-wishlist[data-product-handle], [data-wishlist-toggle], [data-pdp-wishlist], .product-page-wishlist');
    if (btn) lastWishlistClickBtn = btn;
  }, true); /* capture */

  document.addEventListener('wishlist:updated', function () {
    if (!lastWishlistClickBtn) return;
    var btn = lastWishlistClickBtn;
    lastWishlistClickBtn = null;
    /* Defer to next frame so the existing handler's class updates apply first */
    requestAnimationFrame(function () {
      var isActive = btn.classList.contains('text-brand-error') ||
                     btn.getAttribute('aria-pressed') === 'true' ||
                     (btn.querySelector('svg') && btn.querySelector('svg').getAttribute('fill') === 'currentColor');
      if (isActive) {
        heartBurst(btn);
        showToast({ message: 'Saved to wishlist', icon: 'heart' });
      }
    });
  });

  /* ────────────────────────────────────────────────────────────
     3. ADD-TO-CART EFFECTS — 4 versions, switchable via data-cart-effect
        Default: "ghost"
        Options: "ghost" | "image-fly" | "burst" | "morph"
     ──────────────────────────────────────────────────────────── */
  var pendingFlySource = null;
  document.addEventListener('click', function (e) {
    /* Capture the source element BEFORE the cart:open event fires */
    var atc = e.target.closest('.product-card-atc, [data-cart-add-btn], [data-pdp-add-to-cart]');
    if (atc) pendingFlySource = atc;
  }, true);

  function getCartIcon() {
    return document.getElementById('cart-drawer-toggle') || document.getElementById('cart-drawer-badge');
  }

  function effectGhost(sourceEl) {
    /* Small brown circle flies from button → cart icon */
    var cartIcon = getCartIcon();
    if (!cartIcon) return;
    var s = sourceEl.getBoundingClientRect(), d = cartIcon.getBoundingClientRect();
    var sx = s.left + s.width / 2, sy = s.top + s.height / 2;
    var ex = d.left + d.width / 2, ey = d.top + d.height / 2;
    var g = document.createElement('div');
    g.style.cssText =
      'position:fixed;left:' + sx + 'px;top:' + sy + 'px;width:16px;height:16px;border-radius:50%;' +
      'background:#7D6040;box-shadow:0 6px 20px rgba(125,96,64,0.45);' +
      'transform:translate(-50%,-50%) scale(1);opacity:1;pointer-events:none;z-index:9998;' +
      'will-change:transform,opacity;transition:transform 0.7s cubic-bezier(0.55,0,0.4,1),opacity 0.7s ease-in;';
    document.body.appendChild(g);
    requestAnimationFrame(function () {
      g.style.transform = 'translate(' + (ex - sx) + 'px,' + (ey - sy) + 'px) scale(0.3)';
      g.style.opacity = '0';
    });
    setTimeout(function () { g.remove(); }, 800);
  }

  function effectImageFly(sourceEl) {
    /* Clone of nearest product image flies in an arc to the cart icon */
    var cartIcon = getCartIcon();
    if (!cartIcon) return;
    /* Find nearest product image — search up the DOM tree */
    var card = sourceEl.closest('[data-product-card], .product-card, article, li, .group, .grid > div, [class*="product"]') || sourceEl.parentElement;
    var img = card && card.querySelector('img');
    if (!img) return effectGhost(sourceEl); /* fall back if no image */
    var imgRect = img.getBoundingClientRect();
    var d = cartIcon.getBoundingClientRect();
    var sx = imgRect.left + imgRect.width / 2, sy = imgRect.top + imgRect.height / 2;
    var ex = d.left + d.width / 2, ey = d.top + d.height / 2;
    var size = 56;
    var clone = document.createElement('div');
    clone.style.cssText =
      'position:fixed;left:' + sx + 'px;top:' + sy + 'px;width:' + size + 'px;height:' + (size * 1.25) + 'px;' +
      'background-image:url(' + img.currentSrc + ');background-size:cover;background-position:center;' +
      'border-radius:6px;box-shadow:0 12px 36px rgba(54,59,87,0.25);' +
      'transform:translate(-50%,-50%) rotate(0deg) scale(1);opacity:1;pointer-events:none;z-index:9998;' +
      'will-change:transform,opacity;transition:transform 0.85s cubic-bezier(0.34,0,0.4,1),opacity 0.3s ease-in 0.55s;';
    document.body.appendChild(clone);
    /* Apply mid-arc via translate + small rotation, end small at cart */
    requestAnimationFrame(function () {
      clone.style.transform = 'translate(' + (ex - sx) + 'px,' + (ey - sy) + 'px) rotate(8deg) scale(0.18)';
      clone.style.opacity = '0';
    });
    setTimeout(function () { clone.remove(); }, 950);
  }

  function effectBurst(sourceEl) {
    /* Gold particle burst around the button (no fly) */
    var r = sourceEl.getBoundingClientRect();
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    var wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;pointer-events:none;z-index:9999;left:0;top:0;';
    document.body.appendChild(wrap);
    var COLORS = ['#7D6040', '#B8956A', '#D4BC9A', '#8B6E4A'];
    var COUNT = 10, DIST = 50;
    for (var i = 0; i < COUNT; i++) {
      var dot = document.createElement('span');
      var ang = (Math.PI * 2 * i) / COUNT + (Math.random() - 0.5) * 0.3;
      var dist = DIST + Math.random() * 16;
      var dx = Math.cos(ang) * dist, dy = Math.sin(ang) * dist;
      var sz = 5 + Math.random() * 3;
      dot.style.cssText =
        'position:absolute;left:' + cx + 'px;top:' + cy + 'px;width:' + sz + 'px;height:' + sz + 'px;' +
        'border-radius:50%;background:' + COLORS[i % COLORS.length] + ';' +
        'transform:translate(-50%,-50%) scale(0);opacity:1;will-change:transform,opacity;' +
        'transition:transform 0.7s cubic-bezier(0.18,0.89,0.32,1.28),opacity 0.7s ease-out;';
      wrap.appendChild(dot);
      requestAnimationFrame(function (d, x, y) {
        return function () {
          d.style.transform = 'translate(calc(-50% + ' + x + 'px),calc(-50% + ' + y + 'px)) scale(1)';
          setTimeout(function () { d.style.opacity = '0'; d.style.transform = 'translate(calc(-50% + ' + (x * 1.4) + 'px),calc(-50% + ' + (y * 1.4) + 'px)) scale(0.4)'; }, 220);
        };
      }(dot, dx, dy));
    }
    setTimeout(function () { wrap.remove(); }, 900);
  }

  function effectMorph(sourceEl) {
    /* Skip if button already has its own morph handler (product-card-actions.js
       and product-page.js both swap text + bg-green-700 on success). We just
       add the scale-pop accent on top of their morph. */
    var hasBuiltInMorph = sourceEl.classList.contains('bg-green-700') ||
                          sourceEl.classList.contains('bg-green-600') ||
                          (sourceEl.textContent || '').toLowerCase().indexOf('added') > -1;
    if (hasBuiltInMorph) {
      /* Just add a scale pop on top */
      sourceEl.style.transition = 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1)';
      sourceEl.style.transform = 'scale(1.04)';
      setTimeout(function () { sourceEl.style.transform = ''; }, 220);
      return;
    }

    /* Standalone morph (for buttons that don't have built-in handler) */
    var origBg = sourceEl.style.backgroundColor;
    var origText = sourceEl.innerHTML;
    var origWidth = sourceEl.offsetWidth;
    sourceEl.style.minWidth = origWidth + 'px';
    sourceEl.style.transition = 'background-color 0.25s ease,transform 0.25s ease';
    sourceEl.style.backgroundColor = '#3d7a55';
    sourceEl.innerHTML = '<svg width="16" height="16" style="display:inline-block;vertical-align:middle;margin-right:6px" viewBox="0 0 256 256" fill="currentColor"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg>Added';
    sourceEl.style.transform = 'scale(1.03)';
    setTimeout(function () { sourceEl.style.transform = ''; }, 200);
    setTimeout(function () {
      sourceEl.style.backgroundColor = origBg;
      sourceEl.innerHTML = origText;
      sourceEl.style.minWidth = '';
    }, 1600);
  }

  function runAtcEffect(sourceEl) {
    if (prefersReducedMotion || !sourceEl) return;
    /* Default = morph (button-level confirmation); cart drawer auto-opens
       afterwards so user can review + add more or checkout */
    var effect = sourceEl.getAttribute('data-cart-effect') || 'morph';
    if (effect === 'ghost') return effectGhost(sourceEl);
    if (effect === 'image-fly') return effectImageFly(sourceEl);
    if (effect === 'burst') return effectBurst(sourceEl);
    return effectMorph(sourceEl); /* default */
  }

  /* ────────────────────────────────────────────────────────────
     4. TOAST NOTIFICATION (bottom-right)
     Auto-dismisses after 2.6s. Stacks if multiple in quick succession.
     ──────────────────────────────────────────────────────────── */
  var toastWrap = null;
  function getToastWrap() {
    if (toastWrap) return toastWrap;
    toastWrap = document.createElement('div');
    toastWrap.id = 'aykah-toast-wrap';
    toastWrap.setAttribute('aria-live', 'polite');
    toastWrap.setAttribute('aria-atomic', 'true');
    toastWrap.style.cssText =
      'position:fixed;bottom:24px;right:24px;z-index:9999;' +
      'display:flex;flex-direction:column;gap:10px;align-items:flex-end;pointer-events:none;';
    document.body.appendChild(toastWrap);
    return toastWrap;
  }

  function showToast(opts) {
    opts = opts || {};
    var wrap = getToastWrap();
    var t = document.createElement('div');
    var iconHtml = '';
    if (opts.icon === 'cart') {
      iconHtml = '<svg width="16" height="16" style="flex-shrink:0;color:#7D6040" viewBox="0 0 256 256" fill="currentColor"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg>';
    } else if (opts.icon === 'heart') {
      iconHtml = '<svg width="16" height="16" style="flex-shrink:0;color:#d04545" viewBox="0 0 256 256" fill="currentColor"><path d="M178,40c-20.65,0-38.73,8.88-50,23.89C116.73,48.88,98.65,40,78,40a62.07,62.07,0,0,0-62,62c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,228.66,240,172,240,102A62.07,62.07,0,0,0,178,40ZM128,214.8C109.74,204.16,32,155.69,32,102A46.06,46.06,0,0,1,78,56c19.45,0,35.78,10.36,42.6,27a8,8,0,0,0,14.8,0c6.82-16.67,23.15-27,42.6-27a46.06,46.06,0,0,1,46,46C224,155.61,146.24,204.15,128,214.8Z"/></svg>';
    } else {
      iconHtml = '<svg width="16" height="16" style="flex-shrink:0;color:#7D6040" viewBox="0 0 256 256" fill="currentColor"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg>';
    }
    t.style.cssText =
      'pointer-events:auto;background:#fff;color:#363B57;' +
      'border:1px solid rgba(54,59,87,0.10);border-radius:8px;' +
      'padding:11px 16px;display:inline-flex;align-items:center;gap:10px;' +
      'font-size:13px;font-weight:500;letter-spacing:0.01em;' +
      'box-shadow:0 12px 32px rgba(54,59,87,0.14),0 2px 6px rgba(54,59,87,0.06);' +
      'opacity:0;transform:translateY(12px) scale(0.96);' +
      'transition:transform 0.4s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease;' +
      'max-width:320px;will-change:transform,opacity;';
    t.innerHTML = iconHtml + '<span>' + (opts.message || 'Done') + '</span>';
    wrap.appendChild(t);

    requestAnimationFrame(function () {
      t.style.opacity = '1';
      t.style.transform = 'translateY(0) scale(1)';
    });

    setTimeout(function () {
      t.style.opacity = '0';
      t.style.transform = 'translateY(8px) scale(0.96)';
      setTimeout(function () { t.remove(); }, 350);
    }, opts.duration || 2600);
  }

  /* Expose for other scripts to use if they want */
  window.AykahToast = showToast;

  /* ────────────────────────────────────────────────────────────
     5. BUTTON PRESS FEEDBACK
     Subtle scale-down on pointerdown for high-intent buttons.
     ──────────────────────────────────────────────────────────── */
  if (!prefersReducedMotion) {
    var INTENT_SELECTOR = '[data-cart-add-btn], [data-pdp-add-to-cart], .product-card-atc, [data-buy-now-btn], [data-conversion-btn]';
    document.addEventListener('pointerdown', function (e) {
      var btn = e.target.closest(INTENT_SELECTOR);
      if (!btn || btn.disabled) return;
      btn.style.transition = 'transform 0.12s ease-out';
      btn.style.transform = 'scale(0.97)';
    });
    document.addEventListener('pointerup', function (e) {
      var btn = e.target.closest(INTENT_SELECTOR);
      if (!btn) return;
      btn.style.transition = 'transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)';
      btn.style.transform = '';
    });
    document.addEventListener('pointerleave', function (e) {
      var btn = e.target.closest(INTENT_SELECTOR);
      if (!btn) return;
      btn.style.transform = '';
    }, true);
  }

  /* ────────────────────────────────────────────────────────────
     6. WIRE INTO EXISTING EVENTS
     ──────────────────────────────────────────────────────────── */
  document.addEventListener('cart:updated', function () {
    bumpCartBadge();
    /* Fire toast + ATC effect only when ADDING (we have a pending source).
       For "morph", the cart drawer slide-in is the main confirmation, so we
       skip the toast to avoid double-confirming. Other effects get the toast. */
    if (pendingFlySource) {
      runAtcEffect(pendingFlySource);
      var effect = pendingFlySource.getAttribute('data-cart-effect') || 'morph';
      if (effect !== 'morph') {
        showToast({ message: 'Added to cart', icon: 'cart' });
      }
      pendingFlySource = null;
    }
  });

  /* When `cart:open` is dispatched without a preceding click (e.g., manual open),
     don't show toast — only react to add events. */

  /* Reset pending source if click didn't lead to an add within 4s */
  setInterval(function () {
    if (pendingFlySource) {
      var t = pendingFlySource.__capturedAt || 0;
      if (Date.now() - t > 4000) pendingFlySource = null;
    }
  }, 2000);
  document.addEventListener('click', function (e) {
    var atc = e.target.closest('.product-card-atc, [data-cart-add-btn], [data-pdp-add-to-cart]');
    if (atc) atc.__capturedAt = Date.now();
  }, true);

  /* ────────────────────────────────────────────────────────────
     7. FREE-SHIPPING PROGRESS NUDGE (cart drawer)
     If a [data-shipping-progress] bar exists, briefly pulse its fill
     when cart updates and threshold gets closer.
     ──────────────────────────────────────────────────────────── */
  document.addEventListener('cart:updated', function () {
    var bar = document.querySelector('[data-shipping-progress-fill]');
    if (!bar || prefersReducedMotion) return;
    bar.style.transition = 'transform 0.18s ease-out';
    bar.style.transformOrigin = 'left center';
    bar.style.transform = 'scaleY(1.3)';
    setTimeout(function () {
      bar.style.transition = 'transform 0.45s cubic-bezier(0.22,1,0.36,1)';
      bar.style.transform = 'scaleY(1)';
    }, 220);
  });

  /* ────────────────────────────────────────────────────────────
     8. CART DRAWER QTY STEPPER + SUBTOTAL FEEDBACK
     When user clicks +/- inside the cart drawer:
       - Qty number bounces (scale-pop) in the action's direction
       - Subtle gold ring ripples out from the clicked button
     When subtotal text changes (after cart:updated):
       - Flashes gold + scales briefly so the user sees the price update
     ──────────────────────────────────────────────────────────── */
  /* Qty effects — switchable via data-qty-effect on the wrapper or button.
     Default = "press" (cleanest: just qty number bounce + tiny button press).
     Options: 'press' | 'bg-pulse' | 'border-accent' | 'silent' */
  function qtyEffectPress(qtyBtn, qtySpan, action) {
    /* Number bounce */
    if (qtySpan) {
      qtySpan.style.transition = 'none';
      qtySpan.style.display = 'inline-block';
      qtySpan.style.transformOrigin = 'center';
      qtySpan.style.transform = action === 'minus' ? 'scale(0.78)' : 'scale(1.28)';
      requestAnimationFrame(function () {
        qtySpan.style.transition = 'transform 0.42s cubic-bezier(0.34,1.56,0.64,1)';
        qtySpan.style.transform = 'scale(1)';
      });
    }
    /* Tiny button press */
    qtyBtn.style.transition = 'transform 0.12s ease-out';
    qtyBtn.style.transform = 'scale(0.88)';
    setTimeout(function () {
      qtyBtn.style.transition = 'transform 0.32s cubic-bezier(0.34,1.56,0.64,1)';
      qtyBtn.style.transform = '';
    }, 120);
  }
  function qtyEffectBgPulse(qtyBtn, qtySpan, action) {
    if (qtySpan) {
      qtySpan.style.transition = 'none';
      qtySpan.style.display = 'inline-block';
      qtySpan.style.transform = 'scale(1.18)';
      requestAnimationFrame(function () {
        qtySpan.style.transition = 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)';
        qtySpan.style.transform = 'scale(1)';
      });
    }
    var origBg = qtyBtn.style.backgroundColor;
    qtyBtn.style.transition = 'background-color 0.18s ease-out';
    qtyBtn.style.backgroundColor = 'hsl(var(--foreground) / 0.06)';
    setTimeout(function () {
      qtyBtn.style.transition = 'background-color 0.5s ease';
      qtyBtn.style.backgroundColor = origBg;
    }, 200);
  }
  function qtyEffectBorderAccent(qtyBtn, qtySpan, action) {
    if (qtySpan) {
      qtySpan.style.transition = 'none';
      qtySpan.style.display = 'inline-block';
      qtySpan.style.transform = action === 'minus' ? 'scale(0.85)' : 'scale(1.2)';
      requestAnimationFrame(function () {
        qtySpan.style.transition = 'transform 0.45s cubic-bezier(0.34,1.56,0.64,1)';
        qtySpan.style.transform = 'scale(1)';
      });
    }
    var origBorder = qtyBtn.style.borderColor;
    var origColor = qtyBtn.style.color;
    qtyBtn.style.transition = 'color 0.2s ease, border-color 0.2s ease';
    qtyBtn.style.color = '#363B57';
    setTimeout(function () {
      qtyBtn.style.transition = 'color 0.55s ease, border-color 0.55s ease';
      qtyBtn.style.color = origColor;
      qtyBtn.style.borderColor = origBorder;
    }, 250);
  }
  function qtyEffectSilent(qtyBtn, qtySpan, action) {
    /* Number bounce only — no button feedback */
    if (!qtySpan) return;
    qtySpan.style.transition = 'none';
    qtySpan.style.display = 'inline-block';
    qtySpan.style.transform = action === 'minus' ? 'scale(0.78)' : 'scale(1.25)';
    requestAnimationFrame(function () {
      qtySpan.style.transition = 'transform 0.45s cubic-bezier(0.34,1.56,0.64,1)';
      qtySpan.style.transform = 'scale(1)';
    });
  }

  document.addEventListener('click', function (e) {
    if (prefersReducedMotion) return;
    var qtyBtn = e.target.closest('.cart-drawer-qty-btn');
    if (!qtyBtn) return;
    var action = qtyBtn.getAttribute('data-action');
    var qtySpan = qtyBtn.parentElement && qtyBtn.parentElement.querySelector('span');
    var effect = qtyBtn.getAttribute('data-qty-effect') ||
                 (qtyBtn.parentElement && qtyBtn.parentElement.getAttribute('data-qty-effect')) ||
                 'press';
    if (effect === 'bg-pulse') return qtyEffectBgPulse(qtyBtn, qtySpan, action);
    if (effect === 'border-accent') return qtyEffectBorderAccent(qtyBtn, qtySpan, action);
    if (effect === 'silent') return qtyEffectSilent(qtyBtn, qtySpan, action);
    return qtyEffectPress(qtyBtn, qtySpan, action); /* default */
  });

  /* Subtotal effects — switchable via data-subtotal-effect attr.
     Default = "slide" (cleanest editorial feel; replaces the original flash). */
  function parseMoney(str) {
    var n = parseFloat((str || '').replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  }
  function formatMoneyLike(template, value) {
    /* Preserve currency symbol + thousand separators of the template string */
    var symbol = (template.match(/[^\d\s,.\-]/g) || ['$']).join('');
    var rounded = Math.abs(value).toFixed(2);
    var parts = rounded.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (value < 0 ? '−' : '') + symbol + parts.join('.');
  }

  function effectSubtotalSlide(el, oldText, newText) {
    /* Old text slides up + fades out, new text slides in from below */
    var rect = el.getBoundingClientRect();
    el.style.position = 'relative';
    el.style.display = 'inline-block';
    el.style.minHeight = rect.height + 'px';
    el.style.minWidth = rect.width + 'px';
    el.style.overflow = 'hidden';
    /* Snapshot of old text */
    var ghost = document.createElement('span');
    ghost.textContent = oldText;
    ghost.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:flex-end;will-change:transform,opacity;transition:transform 0.4s cubic-bezier(0.55,0,0.4,1),opacity 0.3s ease-out;';
    el.textContent = '';
    el.appendChild(ghost);
    /* New value below */
    var fresh = document.createElement('span');
    fresh.textContent = newText;
    fresh.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:flex-end;transform:translateY(100%);opacity:0;will-change:transform,opacity;transition:transform 0.45s cubic-bezier(0.34,1.56,0.64,1),opacity 0.3s ease-out;';
    el.appendChild(fresh);
    requestAnimationFrame(function () {
      ghost.style.transform = 'translateY(-100%)';
      ghost.style.opacity = '0';
      fresh.style.transform = 'translateY(0)';
      fresh.style.opacity = '1';
    });
    setTimeout(function () {
      el.style.minHeight = '';
      el.style.minWidth = '';
      el.style.overflow = '';
      el.textContent = newText;
    }, 500);
  }

  function effectSubtotalCount(el, oldText, newText) {
    /* Animate the number counting from old → new (700ms). Most informative. */
    var from = parseMoney(oldText);
    var to = parseMoney(newText);
    if (from === null || to === null) { el.textContent = newText; return; }
    var start = performance.now();
    var DUR = 700;
    function tick(now) {
      var t = Math.min(1, (now - start) / DUR);
      var eased = 1 - Math.pow(1 - t, 3); /* ease-out cubic */
      var v = from + (to - from) * eased;
      el.textContent = formatMoneyLike(newText, v);
      if (t < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function effectSubtotalUnderline(el) {
    /* Draws a gold underline beneath the subtotal, 600ms */
    el.style.position = 'relative';
    el.style.display = 'inline-block';
    var line = document.createElement('span');
    line.style.cssText =
      'position:absolute;left:0;right:0;bottom:-3px;height:2px;background:#B8956A;border-radius:2px;' +
      'transform:scaleX(0);transform-origin:left center;will-change:transform,opacity;' +
      'transition:transform 0.45s cubic-bezier(0.22,1,0.36,1),opacity 0.4s ease-in 0.6s;opacity:1;';
    el.appendChild(line);
    requestAnimationFrame(function () { line.style.transform = 'scaleX(1)'; });
    setTimeout(function () { line.style.opacity = '0'; }, 700);
    setTimeout(function () { line.remove(); }, 1100);
  }

  function effectSubtotalGlow(el) {
    /* Soft warm bg highlight pulse (like a text marker) */
    el.style.position = 'relative';
    el.style.display = 'inline-block';
    var bg = document.createElement('span');
    bg.style.cssText =
      'position:absolute;inset:-4px -8px;background:rgba(184,149,106,0.16);border-radius:4px;' +
      'transform:scaleX(0);transform-origin:left center;z-index:-1;will-change:transform,opacity;' +
      'transition:transform 0.35s cubic-bezier(0.22,1,0.36,1),opacity 0.5s ease-out 0.4s;opacity:1;';
    el.appendChild(bg);
    requestAnimationFrame(function () { bg.style.transform = 'scaleX(1)'; });
    setTimeout(function () { bg.style.opacity = '0'; }, 500);
    setTimeout(function () { bg.remove(); }, 1100);
  }

  function effectSubtotalDelta(el, oldText, newText) {
    /* Show +$N or -$N briefly next to the subtotal (most informative) */
    var from = parseMoney(oldText);
    var to = parseMoney(newText);
    el.textContent = newText;
    if (from === null || to === null || from === to) return;
    var delta = to - from;
    var sign = delta > 0 ? '+' : '−';
    var deltaLabel = sign + formatMoneyLike(newText, Math.abs(delta));
    var chip = document.createElement('span');
    chip.textContent = deltaLabel;
    chip.style.cssText =
      'position:absolute;font-size:0.65em;font-weight:600;letter-spacing:0.04em;' +
      'color:' + (delta > 0 ? '#3d7a55' : '#8B6E4A') + ';' +
      'right:calc(100% + 8px);top:50%;transform:translateY(-50%) translateX(8px);opacity:0;' +
      'pointer-events:none;white-space:nowrap;will-change:transform,opacity;' +
      'transition:transform 0.45s cubic-bezier(0.22,1,0.36,1),opacity 0.35s ease-out;';
    el.style.position = 'relative';
    el.style.display = 'inline-block';
    el.appendChild(chip);
    requestAnimationFrame(function () {
      chip.style.transform = 'translateY(-50%) translateX(0)';
      chip.style.opacity = '1';
    });
    setTimeout(function () {
      chip.style.transform = 'translateY(-50%) translateX(-12px)';
      chip.style.opacity = '0';
    }, 1100);
    setTimeout(function () { chip.remove(); }, 1600);
  }

  function effectSubtotalFlash(el) {
    /* Original — gold color shift + tiny scale */
    var origColor = el.style.color;
    el.style.transformOrigin = 'right center';
    el.style.transition = 'color 0.25s ease, transform 0.25s ease';
    el.style.color = '#8B6E4A';
    el.style.transform = 'scale(1.06)';
    setTimeout(function () {
      el.style.transition = 'color 0.55s ease, transform 0.55s cubic-bezier(0.22,1,0.36,1)';
      el.style.color = origColor;
      el.style.transform = '';
    }, 280);
  }

  function runSubtotalEffect(el, oldText, newText) {
    var effect = el.getAttribute('data-subtotal-effect') ||
                 (el.parentElement && el.parentElement.getAttribute('data-subtotal-effect')) ||
                 'delta';
    if (effect === 'count') return effectSubtotalCount(el, oldText, newText);
    if (effect === 'underline') return effectSubtotalUnderline(el);
    if (effect === 'glow') return effectSubtotalGlow(el);
    if (effect === 'slide') return effectSubtotalSlide(el, oldText, newText);
    if (effect === 'flash') return effectSubtotalFlash(el);
    return effectSubtotalDelta(el, oldText, newText); /* default */
  }

  /* Watch all [data-cart-subtotal] / #cart-drawer-subtotal elements for text changes */
  function snapshotSubtotal(el) {
    if (!el) return;
    el.__lastSubtotalText = el.textContent.trim();
  }
  function getSubtotalEls() {
    var els = [].slice.call(document.querySelectorAll('[data-cart-subtotal]'));
    var hero = document.getElementById('cart-drawer-subtotal');
    if (hero && els.indexOf(hero) === -1) els.push(hero);
    return els;
  }
  /* Take initial snapshot once DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { getSubtotalEls().forEach(snapshotSubtotal); });
  } else {
    getSubtotalEls().forEach(snapshotSubtotal);
  }

  document.addEventListener('cart:updated', function () {
    if (prefersReducedMotion) return;
    /* Defer so cart-drawer.js (or any other handler) can update the text first */
    requestAnimationFrame(function () {
      getSubtotalEls().forEach(function (el) {
        var current = el.textContent.trim();
        var last = el.__lastSubtotalText;
        if (typeof last === 'string' && current !== last) {
          runSubtotalEffect(el, last, current);
        }
        el.__lastSubtotalText = current;
      });
    });
  });
})();
